import { useState, useMemo, useEffect, useRef, ReactNode, ComponentProps } from "react";
import styles from "./styles.module.scss";
import { DebouncedInput, MultiSelect, SelectOption } from "../../components/form";
import { useMultiSelect } from "@/services/filters";
import { uniqueId } from "lodash";
import { BetterPopover } from "../popover";
import classNames from "classnames";
import { ScrollContainer } from "../../components/scrollContainer";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { getVisibleTextFromNode } from "@/helpers/helperUtils";
import { Button, Stack } from "@altimateai/lego";
import { PopoverContent } from "@altimateai/lego";

interface FilterDropdownProps {
  label: ReactNode;
  children: ReactNode;
  options: SelectOption[];
  onChange: (values: string[]) => void;
  value: string[];
  popoverContentProps?: ComponentProps<typeof PopoverContent>;
  disabled?: boolean;
  showSearch?: boolean;
  searchBy?: "label" | "value";
  disableAllDeselect?: boolean;
}

export const FilterDropdown = ({
  label,
  children,
  options,
  onChange,
  value,
  popoverContentProps = {},
  disabled = false,
  showSearch = true,
  searchBy = "label",
  disableAllDeselect = false,
}: FilterDropdownProps) => {
  const id = useMemo(() => uniqueId("filter-dropdown-"), []);
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>(options);
  const defaultMap = useMemo(() => {
    const _default = new Map();
    value.forEach(v => _default.set(v, true));
    return _default;
  }, [value]);
  const { getValue, toggleValue, getTrueValues } = useMultiSelect(() => {}, defaultMap);
  const [showAllDeselectWarning, setShowAllDeselectWarning] = useState(false);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Use useRef instead of useState

  // Keep filteredOptions in sync when the parent passes a new options list.
  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    return () => {
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);

  const handleApply = (closeFn: () => void) => {
    const trueValues = getTrueValues();
    if (disableAllDeselect && trueValues.length === 0) {
      // Clear any existing timeout
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }

      // Show warning and set a new timeout
      setShowAllDeselectWarning(true);
      warningTimeoutRef.current = setTimeout(() => {
        setShowAllDeselectWarning(false);
        warningTimeoutRef.current = null;
      }, 5000);
      return;
    }

    // Clear timeout if the user applies valid selections
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    closeFn();
    onChange(trueValues);
  };

  return (
    <BetterPopover
      id={`popover-${id}`}
      popoverContentProps={{
        // setting this to false to make sure popover always opens at bottom
        avoidCollisions: false,
        side: "bottom",
        align: "end",
        ...popoverContentProps,
      }}
      trigger={
        <Stack gap={"sm"} className="al-cursor-pointer">
          {children}
        </Stack>
      }
      render={({ close }) => (
        <div className="al-min-w-64">
          <Stack justify={"between"} align={"center"} gap={"sm"} className="al-m-2">
            <div>{label}</div>
            <Button
              size="sm"
              variant={"secondary"}
              disabled={disabled}
              onClick={e => {
                e.stopPropagation();
                handleApply(close);
              }}
            >
              Apply
            </Button>
          </Stack>
          <div className="divider al-w-full" />
          {showAllDeselectWarning && (
            <Stack align={"center"} justify={"center"} className={"al-p-2 al-text-action-red"}>
              <span className={"al-text-center"}>At least one column must be selected.</span>
            </Stack>
          )}
          {showSearch && (
            <div className="m-2">
              <DebouncedInput
                size="sm"
                placeholder="Search"
                aria-label="Search options"
                onChange={v => {
                  const searchQuery = v.toLowerCase();
                  if (searchBy === "label") {
                    setFilteredOptions(
                      options.filter(item => {
                        const label = item?.label;
                        if (typeof label === "string") {
                          return label.toLowerCase().includes(searchQuery);
                        } else {
                          const text = getVisibleTextFromNode(label);
                          return text.toLowerCase().includes(searchQuery);
                        }
                      })
                    );
                  } else if (searchBy === "value") {
                    setFilteredOptions(
                      options.filter(item => {
                        const value = item?.value;
                        if (typeof value === "string") {
                          return value.toLowerCase().includes(searchQuery);
                        } else {
                          const text = getVisibleTextFromNode(value);
                          return text.toLowerCase().includes(searchQuery);
                        }
                      })
                    );
                  }
                }}
              />
            </div>
          )}
          <div className={styles.scroll_component}>
            <ScrollContainer>
              <div className={styles.scroll_content}>
                <MultiSelect
                  options={filteredOptions.map(opt => ({
                    ...opt,
                    label:
                      typeof opt.label === "string" ? opt.label : getVisibleTextFromNode(opt.label),
                  }))}
                  onChange={toggleValue}
                  value={getValue}
                  showDivider={false}
                />
              </div>
            </ScrollContainer>
          </div>
        </div>
      )}
    />
  );
};

interface StaticFilterDropdownProps {
  id?: string;
  label: ReactNode;
  options: SelectOption[];
  onChange: (values: string[]) => void;
  value: string[];
  disabled?: boolean;
  showSearch?: boolean;
  searchKey?: string;
}

export const StaticFilterDropdown = ({
  id = "testID",
  label,
  options,
  onChange,
  value,
  disabled = false,
  showSearch = true,
  searchKey,
}: StaticFilterDropdownProps) => {
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>([]);
  const [selectedValues, setSelectedValues] = useState<Map<string, boolean>>(new Map());
  const { updateSearchParamsByKey } = useFiltersBySearchParams();

  useEffect(() => {
    const _default = new Map();
    value.forEach(v => _default.set(v, true));
    setSelectedValues(_default);
  }, [value]);

  const toggleValue = (val: string) => {
    setSelectedValues(prev => {
      const newMap = new Map(prev);
      if (newMap.has(val)) {
        newMap.delete(val);
      } else {
        newMap.set(val, true);
      }
      return newMap;
    });
  };

  const getTrueValues = () => {
    return Array.from(selectedValues.keys());
  };

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  return (
    <div
      id={id}
      className={classNames(styles.dropdown_container, styles.async_container)}
      aria-label={typeof label === "string" ? `${label} filter dropdown` : "filter dropdown"}
    >
      <Stack justify="between" align="center" gap="sm" className="m-2">
        <div>{label}</div>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={e => {
            e.stopPropagation();
            if (searchKey) {
              updateSearchParamsByKey({ [searchKey]: getTrueValues() });
            }
            onChange(getTrueValues());
          }}
        >
          Apply
        </Button>
      </Stack>
      <div className="divider al-w-full" />
      {showSearch && (
        <div className="m-2">
          <DebouncedInput
            size="sm"
            placeholder="Search"
            aria-label="Search options"
            onChange={v => {
              const searchQuery = v.toLowerCase();
              setFilteredOptions(
                options.filter(item => {
                  const label = item?.label;
                  if (typeof label === "string") {
                    return label.toLowerCase().includes(searchQuery);
                  } else {
                    const text = getVisibleTextFromNode(label);
                    return text.toLowerCase().includes(searchQuery);
                  }
                })
              );
            }}
          />
        </div>
      )}
      <div className={styles.scroll_component}>
        <ScrollContainer>
          <div className={styles.scroll_content}>
            <MultiSelect
              options={filteredOptions.map(opt => ({
                ...opt,
                label:
                  typeof opt.label === "string" ? opt.label : getVisibleTextFromNode(opt.label),
              }))}
              onChange={toggleValue}
              value={val => selectedValues.has(val)}
              showDivider={false}
            />
          </div>
        </ScrollContainer>
      </div>
    </div>
  );
};
